import React, { useContext, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Context } from "../../context/Context";
import { Button, XCircle } from "../ui";
import { useModalLayer } from "./modalHooks";
import "./LudDashboardModal.css";

export default function LudDashboardModal({
  open,
  title,
  children,
  onClose,
  footer,
  size = "md",
  // While true (e.g. a save is running) Escape, backdrop and the close button
  // are disabled so the modal can't be dismissed mid-operation.
  busy = false,
}) {
  const { lang } = useContext(Context) || {};
  const titleId = useId();
  const dialogRef = useRef(null);
  const requestClose = () => {
    if (!busy) onClose?.();
  };

  useModalLayer(open, { onEscape: requestClose, containerRef: dialogRef });

  if (!open) return null;

  const closeLabel = lang === "ar" ? "إغلاق" : "Close";

  // Rendered in a portal so `position: fixed` is always relative to the
  // viewport, never to a transformed/clipped ancestor (cards, sections).
  // `dir` is set here because the portal leaves the dashboard shell's dir.
  return createPortal(
    <div
      className="lud-dashboard-modal-backdrop"
      role="presentation"
      dir={lang === "ar" ? "rtl" : "ltr"}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`lud-dashboard-modal lud-dashboard-modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
      >
        <div className="lud-dashboard-modal-header">
          <h3 id={titleId}>{title}</h3>
          <Button
            variant="danger"
            appearance="soft"
            icon={<XCircle size={18} />}
            size="sm"
            onClick={requestClose}
            disabled={busy}
            aria-label={closeLabel}
            title={closeLabel}
          />
        </div>

        <div className="lud-dashboard-modal-body">{children}</div>

        {footer && <div className="lud-dashboard-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
